/**
 * varSymbology — single source of truth for the VAR map symbology and the
 * weather station map sprite. Shared by VARLayer, WeatherStationLayer, and
 * the legend (customLegends) so they can't drift.
 *
 * Visual grammar (Solution B, 2026-06-27):
 *   shape  = VAR category (square / diamond / pentagon) or station (teardrop)
 *   fill   = neutral grey baseline
 *   border = red if flagged
 *   badge  = blue droplet (top-right) if sprinklered
 *
 * Sprites are pre-rendered by `scripts/generate-var-sprites.js` and live in
 * `public/var-sprites/`. There are 12 VAR variants (3 shapes × 4 states) plus
 * 1 station teardrop. Each sprite is the same 24×24 viewBox so MapLibre's
 * icon-size interpolation behaves identically across the set.
 */
import type { VARCategory } from '../../types/var';

/** Shape suffix used in sprite filenames per VAR category. */
export const VAR_SHAPE_SUFFIX: Record<VARCategory, string> = {
  'Infrastructure':    'tri',
  'Cultural Resource': 'di',
  'Natural Resource':  'pent',
};

/** Categories in legend display order. */
export const VAR_CATEGORIES: VARCategory[] = [
  'Infrastructure',
  'Cultural Resource',
  'Natural Resource',
];

/** Sprite IDs registered with `map.addImage()` — match the SVG filenames. */
export const VAR_SPRITE_IDS = {
  // 12 VAR variants
  'var-tri-0': '/var-sprites/var-tri-0.svg',
  'var-tri-s': '/var-sprites/var-tri-s.svg',
  'var-tri-f': '/var-sprites/var-tri-f.svg',
  'var-tri-sf': '/var-sprites/var-tri-sf.svg',
  'var-di-0': '/var-sprites/var-di-0.svg',
  'var-di-s': '/var-sprites/var-di-s.svg',
  'var-di-f': '/var-sprites/var-di-f.svg',
  'var-di-sf': '/var-sprites/var-di-sf.svg',
  'var-pent-0': '/var-sprites/var-pent-0.svg',
  'var-pent-s': '/var-sprites/var-pent-s.svg',
  'var-pent-f': '/var-sprites/var-pent-f.svg',
  'var-pent-sf': '/var-sprites/var-pent-sf.svg',
  // Stations use the FA `cloud-rain` icon (loaded at runtime by VARLayer) so
  // they match the WFS Wx stations layer — no local sprite needed.
} as const;

export type VarSpriteId = keyof typeof VAR_SPRITE_IDS;

/** Palette used by the legend swatches and any non-sprite UI rendering. */
export const VAR_SYMBOLOGY_COLORS = {
  baseline:    '#6b7280',  // grey-500 — sprite fill
  flagBorder:  '#ef4444',  // red-500   — flagged border
  sprinklerBadge: '#2563eb', // blue-600 — sprinkler droplet
  haloWhite:   '#ffffff',
} as const;

/** Zoom breakpoints — tweak freely without other refactoring. */
export const VAR_ZOOM = {
  /** Dots below this zoom; symbol icons above. */
  dotToSymbol: 6,
  /**
   * VAR ID label appears at or above this zoom. Set to dotToSymbol so the
   * moment a feature renders as a shape (post-cluster), its var_id label
   * appears too. Density is managed by clustering, not by label suppression.
   */
  label: 6,
} as const;

